/**
 * PowerSavingSwitcher component.
 */
import Direction from '../../../../shared/types/enums/direction';
import Focus from '../../../../shared/types/enums/focus';
import Storage from '@storage';
import SwitcherContainer from '../../atoms/container/switcherContainer';
import SwitcherBall from '../../atoms/container/switcherBall';
import useKeyboard from '../../../../shared/components/utils/hooks/useKeyboard';
import useTransform from '../../../../shared/components/utils/hooks/useTransform';
import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../../../shared/store/hooks';
import { setFocus } from '../../../../shared/store/slices/focus.slice';

/**
 * Constants
 */
const SWITCH_OFFSET = -107;

/**
 * PowerSavingSwitcher component.
 * @param active - Whether the switcher is active.
 * @returns {JSX.Element} The PowerSavingSwitcher component.
 */
export default function PowerSavingSwitcher({ active }: { active: boolean }) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Refs
  const activeReference = useRef(active ? true : false);

  // Transform
  const [style, change] = useTransform(
    active ? 1 : 0,
    SWITCH_OFFSET,
    Direction.HORIZONTAL
  );

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const switchAction = useCallback(() => {
    activeReference.current = !activeReference.current;
    change(activeReference.current ? 1 : -1);
    Storage.set('deadmanActive', activeReference.current ? '1' : '0');
  }, []);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onClick = useCallback(() => {
    switchAction();
  }, [switchAction]);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.POWER_SAVING));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Power saving keyboard
  useKeyboard({
    focus: Focus.POWER_SAVING,
    keyDownHandler(key) {
      if (key === 'SELECT') {
        switchAction();
      }
    },
  });

  return (
    <SwitcherContainer id={'switcherContainer'} onClick={onClick}>
      <SwitcherBall style={style} active={activeReference.current} />
    </SwitcherContainer>
  );
}
